import { useState, useRef, useEffect, useCallback } from "react";
import { Clapperboard, BookOpen, Mic, Square, Sparkles, Loader2, Plus, Play, Link2, Link2Off, Users, Upload, X, Check, Film, Download, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { directorApi, pipelinesApi, streamAsset } from "@/api/mist";
import SaveDestinations from "@/components/gallery/SaveDestinations";

/* ==========================================================================
   DIRECTOR STUDIO V2 — one unified scene-array workspace.
   Global view flag maps the SAME scene array between CINEMATIC (video blocks)
   and COMIC (static graphic panels). Dialogue / voice / continuity / cast live
   natively here. NO auto-save — a scene is written only on NEXT SCENE.
   ========================================================================== */
const GOLD = "#FFE08A";
const VOICES = [
  { id: "onyx", label: "Onyx · M" }, { id: "echo", label: "Echo · M" }, { id: "fable", label: "Fable · M" },
  { id: "nova", label: "Nova · F" }, { id: "shimmer", label: "Shimmer · F" }, { id: "alloy", label: "Alloy · N" },
];
const CINE_DURATIONS = ["6", "8", "10"];
const COMIC_STYLE_OPTIONS = [
  { k: "disney_3d", label: "3D Disney Game" },
  { k: "retro_newsprint", label: "Retro Newsprint Strip" },
  { k: "superhero_splash", label: "Bold Superhero Splash" },
  { k: "indie_watercolor", label: "Indie Watercolor Narrative" },
  { k: "chibi_vinyl", label: "Chibi Vinyl Mini-Game Art" },
];

const emptyDraft = () => ({
  prompt: "", dialogue: "", speaker: "onyx", continuityLock: false, characterId: "",
  comicStyle: "disney_3d", animate: false,
  duration: "6", previewSrc: null, previewMode: "", outputAssetId: "", terminalId: "",
  aiFixed: "", engineSeed: 0,
});

export default function DirectorProjectStudio() {
  const [projectId, setProjectId] = useState(null);
  const [projectName, setProjectName] = useState("Untitled Project");
  const [viewMode, setViewMode] = useState("CINEMATIC");   // global flag
  const [scenes, setScenes] = useState([]);                 // saved (DB) scenes
  const [draft, setDraft] = useState(emptyDraft());
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState("");
  const [exporting, setExporting] = useState(false);
  const [exportAsset, setExportAsset] = useState(null);
  const [cast, setCast] = useState([]);
  const [recording, setRecording] = useState(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const upRef = useRef(null);

  const isComic = viewMode === "COMIC";
  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));

  useEffect(() => { directorApi.listCast().then((r) => setCast(r.cast || [])).catch(() => {}); }, []);

  const ensureProject = useCallback(async () => {
    if (projectId) return projectId;
    const r = await directorApi.createProject(projectName || "Untitled Project");
    setProjectId(r.project_id);
    return r.project_id;
  }, [projectId, projectName]);

  /* ---------------- STEP 1: Whisper (record → transcript) ---------------- */
  const toggleRecord = async () => {
    if (recording) { recRef.current?.stop(); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setStage("Transcribing…"); setBusy(true);
        try {
          const r = await directorApi.transcribe(blob);
          if (r.transcript) { set({ dialogue: r.transcript }); toast.success("Voice transcribed into dialogue"); }
          else toast.error("Couldn't hear anything to transcribe");
        } catch { toast.error("Transcription failed"); }
        finally { setBusy(false); setStage(""); }
      };
      mr.start(); recRef.current = mr; setRecording(true);
    } catch { toast.error("Microphone unavailable"); }
  };

  /* ---------------- Cast: upload a face → save to global cast ------------- */
  const uploadFace = async (e) => {
    const f = (e.target.files || [])[0]; e.target.value = "";
    if (!f) return;
    setBusy(true); setStage("Uploading character…");
    try {
      const up = await pipelinesApi.uploadSeed(f);
      const name = (f.name.split(".")[0] || "Character").slice(0, 20);
      const c = await directorApi.saveCast({ name, reference_asset_id: up.asset_id, voice: draft.speaker, description: name });
      setCast((prev) => [c, ...prev]);
      set({ characterId: c.id });
      toast.success(`${name} added to your cast`);
    } catch { toast.error("Character upload failed"); }
    finally { setBusy(false); setStage(""); }
  };

  const deleteCastMember = async (id) => {
    try {
      await directorApi.deleteCast(id);
      setCast((prev) => prev.filter((c) => c.id !== id));
      if (draft.characterId === id) set({ characterId: "" });
      toast.success("Character removed");
    } catch { toast.error("Couldn't remove character"); }
  };

  /* ---------------- AI FIX — manual prompt optimizer (Emergent LLM) ------ */
  const handleAIPromptFix = async () => {
    if (!draft.prompt.trim()) { toast.error("Type a scene prompt to optimize first"); return; }
    setBusy(true); setStage("Optimizing prompt…");
    try {
      const r = await directorApi.aiFix(draft.prompt.trim(), draft.characterId);
      if (r.text) { set({ prompt: r.text, aiFixed: r.text }); toast.success("Prompt optimized"); }
    } catch (e) { toast.error(e?.response?.data?.detail || "AI Fix failed"); }
    finally { setBusy(false); setStage(""); }
  };

  /* ---------------- GENERATE (steps 1-4 backend pipeline) ---------------- */
  const generate = async () => {
    if (!draft.prompt.trim()) { toast.error("Describe the scene first"); return; }
    setBusy(true); setStage("Starting…"); setExportAsset(null);
    try {
      const pid = await ensureProject();
      const sceneObj = buildScene(draft, viewMode, scenes.length);
      const { job_id } = await directorApi.generate(pid, sceneObj);
      const STAGES = { reading: "Reading…", prompt: "Expanding prompt…", rendering: "Rendering image…", animating: "Animating (MiniMax H3)…" };
      const j = await directorApi.poll(job_id, { onTick: (x) => x.stage && setStage(STAGES[x.stage] || "Working…") });
      if (j.status !== "completed" || !j.asset_id) throw new Error(j.error || "Generation failed");
      const src = await streamAsset(j.asset_id);
      set({
        previewSrc: src, previewMode: j.mode || (isComic ? "image" : "video"),
        outputAssetId: j.asset_id, terminalId: j.terminal_frame_asset_id || "",
        aiFixed: j.ai_fixed_prompt || "", engineSeed: j.engine_seed || 0,
      });
      toast.success(isComic ? "Panel drawn" : "Scene rendered");
    } catch (e) { toast.error(e?.response?.data?.detail || e.message || "Generation failed"); }
    finally { setBusy(false); setStage(""); }
  };

  /* ---------------- NEXT SCENE — the ONLY hard save ---------------------- */
  const nextScene = async () => {
    if (!draft.outputAssetId) { toast.error("Generate this scene before saving"); return; }
    setBusy(true); setStage("Saving scene…");
    try {
      const pid = await ensureProject();
      const sceneObj = buildScene(draft, viewMode, scenes.length, true);
      await directorApi.saveScene(pid, sceneObj);
      setScenes((prev) => [...prev, { ...sceneObj, _src: draft.previewSrc }]);
      // Continuity ON carries everything forward (character ref, last frame, voice,
      // and stays chained). OFF = a clean new scene — references are wiped and the
      // user must upload/lock a fresh character for the next scene.
      if (draft.continuityLock) {
        setDraft({
          ...emptyDraft(),
          duration: isComic ? "STATIC" : "6",
          continuityLock: true,
          speaker: draft.speaker,
          characterId: draft.characterId,
          terminalId: draft.terminalId,
        });
      } else {
        setDraft({ ...emptyDraft(), duration: isComic ? "STATIC" : "6", continuityLock: false });
      }
      toast.success(`Scene ${scenes.length + 1} saved`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); setStage(""); }
  };

  /* ---------------- EXPORT — 1080x1920 master --------------------------- */
  const doExport = async () => {
    if (scenes.length === 0) { toast.error("Save at least one scene first"); return; }
    setExporting(true); setStage("Baking 1080×1920 master…"); setExportAsset(null);
    try {
      const { job_id } = await directorApi.export(projectId);
      const j = await directorApi.poll(job_id, { onTick: (x) => x.stage && setStage(x.stage) });
      if (j.status !== "completed" || !j.asset_id) throw new Error(j.error || "Export failed");
      const src = await streamAsset(j.asset_id);
      setExportAsset({ id: j.asset_id, src });
      toast.success("Master reel ready");
    } catch (e) { toast.error(e?.response?.data?.detail || e.message || "Export failed"); }
    finally { setExporting(false); setStage(""); }
  };

  return (
    <div className="h-full w-full overflow-y-auto bg-[#0A0A0A]" data-testid="director-project-studio">
      <div className="mx-auto max-w-2xl space-y-4 p-4 pb-28">
        {/* Project name + global view flag */}
        <div className="flex flex-col gap-3 border-2 border-black bg-[#111] p-3 shadow-[3px_3px_0px_#000]">
          <input value={projectName} onChange={(e) => setProjectName(e.target.value.slice(0, 80))}
            data-testid="dps-project-name"
            className="w-full bg-transparent border-b-2 border-white/10 focus:border-[#FFE08A] text-white font-serif text-lg px-1 py-1 outline-none"
            placeholder="Untitled Project" />
          <div className="grid grid-cols-2 gap-2" data-testid="dps-viewmode">
            <button onClick={() => { setViewMode("CINEMATIC"); set({ duration: "6" }); }} data-testid="dps-mode-cinematic"
              className={`flex items-center justify-center gap-1.5 border-2 border-black py-2.5 font-mono text-[10px] font-black uppercase tracking-widest ${!isComic ? "bg-[#FFE08A] text-black" : "bg-[#1a1a1a] text-white/50"}`}>
              <Clapperboard size={14} /> Cinematic
            </button>
            <button onClick={() => { setViewMode("COMIC"); set({ animate: false }); }} data-testid="dps-mode-comic"
              className={`flex items-center justify-center gap-1.5 border-2 border-black py-2.5 font-mono text-[10px] font-black uppercase tracking-widest ${isComic ? "bg-[#FFE08A] text-black" : "bg-[#1a1a1a] text-white/50"}`}>
              <BookOpen size={14} /> Comic
            </button>
          </div>
        </div>

        {/* Saved scene rail (chronological) */}
        {scenes.length > 0 && (
          <div>
            <p className="mb-1.5 font-mono text-[9px] font-black uppercase tracking-widest text-white/50">Scenes · {scenes.length}</p>
            <div className="flex gap-2 overflow-x-auto pb-1" data-testid="dps-scene-rail">
              {scenes.map((s, i) => (
                <div key={i} data-testid={`dps-scene-${i}`} className="relative w-16 shrink-0 aspect-[9/16] border-2 border-black bg-[#161616] overflow-hidden">
                  {s._src ? (s.visuals?.mode === "video"
                    ? <video src={s._src} muted loop playsInline className="h-full w-full object-cover" />
                    : <img src={s._src} alt={`Scene ${i + 1}`} className="h-full w-full object-cover" />) : null}
                  <span className="absolute left-0.5 top-0.5 grid h-4 min-w-4 place-items-center bg-[#FFE08A] px-0.5 font-mono text-[8px] font-black text-black">{i + 1}</span>
                  {s.continuity_lock && <Link2 size={10} className="absolute right-0.5 top-0.5 text-[#00F5D4]" />}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Current scene editor */}
        <div className="space-y-3 border-2 border-black bg-[#111] p-3 shadow-[3px_3px_0px_#000]">
          <p className="font-mono text-[10px] font-black uppercase tracking-widest text-[#FFE08A]">
            {isComic ? "New Panel" : "New Scene"} {scenes.length + 1}
          </p>

          {/* Preview */}
          <div className="relative border-2 border-black bg-black aspect-[9/16] max-h-[46vh] mx-auto overflow-hidden" style={{ width: "min(100%, 26vh)" }}>
            {draft.previewSrc ? (
              draft.previewMode === "video"
                ? <video src={draft.previewSrc} data-testid="dps-preview-video" controls autoPlay loop playsInline className="h-full w-full object-contain" />
                : <img src={draft.previewSrc} alt="preview" data-testid="dps-preview-image" className="h-full w-full object-contain" />
            ) : (
              <div className="grid h-full place-items-center px-3 text-center font-mono text-[9px] text-white/40">
                {busy ? <div className="flex flex-col items-center gap-2"><Loader2 size={20} className="animate-spin text-[#FFE08A]" />{stage}</div> : "Describe & Generate this scene"}
              </div>
            )}
          </div>

          {/* Per-panel animation gate (COMIC only) — default OFF = still PNG */}
          {isComic && (
            <div className="flex items-center gap-2 border-2 border-black bg-[#1a1a1a] p-2" data-testid="dps-animate-row">
              <button onClick={() => set({ animate: !draft.animate })} data-testid="dps-animate"
                aria-pressed={draft.animate}
                className={`flex items-center gap-2 border-2 border-black px-2.5 py-2 font-mono text-[9px] font-black uppercase tracking-widest ${draft.animate ? "bg-[#00F5D4] text-black" : "bg-[#111] text-white/50"}`}>
                <Film size={13} />
                <span className="flex flex-col items-start leading-none">
                  <span>Animate Scene</span>
                  <span className="text-[7px] opacity-70">{draft.animate ? "On · MiniMax H3" : "Default: Off · still panel"}</span>
                </span>
                <span className={`grid h-4 w-7 items-center rounded-full border-2 border-black px-0.5 ${draft.animate ? "bg-black" : "bg-white/20"}`}>
                  <span className={`h-2.5 w-2.5 rounded-full transition-transform ${draft.animate ? "translate-x-3 bg-[#00F5D4]" : "translate-x-0 bg-white/60"}`} />
                </span>
              </button>
              {draft.animate ? (
                <div className="flex flex-1 items-center gap-1.5" data-testid="dps-comic-durations">
                  {CINE_DURATIONS.map((d) => (
                    <button key={d} onClick={() => set({ duration: d })} data-testid={`dps-comic-dur-${d}`}
                      className={`flex-1 border-2 border-black py-2 font-mono text-[10px] font-black ${draft.duration === d ? "bg-[#FFE08A] text-black" : "bg-[#111] text-white/50"}`}>{d}s</button>
                  ))}
                </div>
              ) : (
                <span className="flex-1 text-right font-mono text-[8px] font-black uppercase tracking-widest text-white/30">Static .png</span>
              )}
            </div>
          )}

          {/* Prompt */}
          <textarea value={draft.prompt} onChange={(e) => set({ prompt: e.target.value.slice(0, 600) })} rows={2}
            data-testid="dps-prompt" placeholder="What happens in this scene?"
            className="w-full resize-none border-2 border-black bg-[#1a1a1a] p-2.5 font-mono text-xs text-white outline-none focus:border-[#FFE08A]" />

          {/* Dialogue + mic + speaker */}
          <div className="border-2 border-black bg-[#1a1a1a] p-2.5 space-y-2">
            <div className="flex items-center gap-2 font-mono text-[9px] font-black uppercase tracking-widest text-white/60"><Mic size={12} /> Dialogue & Voice</div>
            <textarea value={draft.dialogue} onChange={(e) => set({ dialogue: e.target.value.slice(0, 500) })} rows={2}
              data-testid="dps-dialogue" placeholder="Spoken line (baked as voiceover on export)…"
              className="w-full resize-none border-2 border-black bg-[#111] p-2 font-mono text-xs text-white outline-none focus:border-[#FFE08A]" />
            <div className="flex items-center gap-2">
              <button onClick={handleAIPromptFix} disabled={busy || !draft.prompt.trim()} data-testid="dps-ai-fix"
                className="flex items-center gap-1.5 border-2 border-black bg-[#FFE08A] px-2.5 py-2 font-mono text-[9px] font-black uppercase tracking-widest text-black disabled:opacity-50">
                <Wand2 size={11} /> AI Fix
              </button>
              <button onClick={toggleRecord} data-testid="dps-record"
                className={`flex items-center gap-1.5 border-2 border-black px-2.5 py-2 font-mono text-[9px] font-black uppercase tracking-widest ${recording ? "bg-red-500 text-white" : "bg-[#00F5D4] text-black"}`}>
                {recording ? <><Square size={11} /> Stop</> : <><Mic size={11} /> Record</>}
              </button>
              <select value={draft.speaker} onChange={(e) => set({ speaker: e.target.value })} data-testid="dps-speaker"
                className="flex-1 border-2 border-black bg-[#111] p-2 font-mono text-[10px] font-black uppercase text-white outline-none">
                {VOICES.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
              </select>
            </div>
          </div>

          {/* Cast (character identity lock) */}
          <div className="border-2 border-black bg-[#1a1a1a] p-2.5 space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 font-mono text-[9px] font-black uppercase tracking-widest text-white/60"><Users size={12} /> Character Lock</div>
              <input ref={upRef} type="file" accept="image/*" hidden onChange={uploadFace} data-testid="dps-cast-upload-input" />
              <button onClick={() => upRef.current?.click()} data-testid="dps-cast-upload" className="flex items-center gap-1 border-2 border-black bg-[#FFE08A] px-2 py-1 font-mono text-[8px] font-black uppercase tracking-widest text-black"><Upload size={10} /> Add</button>
            </div>
            {cast.length > 0 ? (
              <div className="flex flex-wrap gap-1.5" data-testid="dps-cast-list">
                {cast.map((c) => {
                  const on = draft.characterId === c.id;
                  return (
                    <div key={c.id} className={`flex items-center border-2 border-black ${on ? "bg-[#FFE08A]" : "bg-[#111]"}`}>
                      <button onClick={() => set({ characterId: on ? "" : c.id, speaker: c.voice || draft.speaker })} data-testid={`dps-cast-${c.id}`}
                        className={`flex items-center gap-1 px-2 py-1.5 font-mono text-[9px] font-black uppercase ${on ? "text-black" : "text-white/60"}`}>
                        {on ? <Check size={10} strokeWidth={3} /> : <Users size={10} />} {c.name}
                      </button>
                      <button onClick={() => deleteCastMember(c.id)} data-testid={`dps-cast-del-${c.id}`}
                        title="Remove character"
                        className={`grid h-full place-items-center border-l-2 border-black px-1.5 ${on ? "text-black/60 hover:text-black" : "text-white/40 hover:text-red-400"}`}>
                        <X size={11} strokeWidth={3} />
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : <p className="font-mono text-[8px] text-white/30">No cast yet — add a face to lock a recurring character.</p>}

            {/* Comic style selector (5 locked non-realistic styles) */}
            {isComic && (
              <div className="border-t-2 border-white/10 pt-2" data-testid="dps-comic-style-row">
                <p className="mb-1 font-mono text-[8px] font-black uppercase tracking-widest text-white/50">Comic Style</p>
                <div className="relative">
                  <select value={draft.comicStyle} onChange={(e) => set({ comicStyle: e.target.value })} data-testid="dps-comic-style"
                    className="w-full appearance-none border-2 border-[#FFE08A] bg-[#111] px-2.5 py-2 pr-8 font-mono text-[11px] font-black uppercase tracking-wide text-[#FFE08A] outline-none focus:border-[#00F5D4]">
                    {COMIC_STYLE_OPTIONS.map((s) => <option key={s.k} value={s.k} className="bg-[#111] text-white normal-case">{s.label}</option>)}
                  </select>
                  <BookOpen size={13} className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-[#00F5D4]" />
                </div>
              </div>
            )}
          </div>

          {/* Continuity (+ duration for Cinematic; comic duration lives in the animate row) */}
          {isComic ? (
            <button onClick={() => set({ continuityLock: !draft.continuityLock })} data-testid="dps-continuity"
              className={`flex w-full items-center justify-center gap-1.5 border-2 border-black py-2.5 font-mono text-[9px] font-black uppercase tracking-widest ${draft.continuityLock ? "bg-[#00F5D4] text-black" : "bg-[#1a1a1a] text-white/50"}`}>
              {draft.continuityLock ? <Link2 size={13} /> : <Link2Off size={13} />} Continuity {draft.continuityLock ? "On" : "Off"}
            </button>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              <button onClick={() => set({ continuityLock: !draft.continuityLock })} data-testid="dps-continuity"
                className={`flex items-center justify-center gap-1.5 border-2 border-black py-2.5 font-mono text-[9px] font-black uppercase tracking-widest ${draft.continuityLock ? "bg-[#00F5D4] text-black" : "bg-[#1a1a1a] text-white/50"}`}>
                {draft.continuityLock ? <Link2 size={13} /> : <Link2Off size={13} />} Continuity {draft.continuityLock ? "On" : "Off"}
              </button>
              <div className="flex items-center gap-1.5">
                {CINE_DURATIONS.map((d) => (
                  <button key={d} onClick={() => set({ duration: d })} data-testid={`dps-duration-${d}`}
                    className={`flex-1 border-2 border-black py-2.5 font-mono text-[10px] font-black ${draft.duration === d ? "bg-[#FFE08A] text-black" : "bg-[#1a1a1a] text-white/50"}`}>{d}s</button>
                ))}
              </div>
            </div>
          )}

          {/* Generate + Next scene */}
          <div className="grid grid-cols-2 gap-2">
            <button onClick={generate} disabled={busy || !draft.prompt.trim()} data-testid="dps-generate"
              className="flex items-center justify-center gap-2 border-2 border-black bg-[#FFE08A] py-3 font-mono text-xs font-black uppercase tracking-widest text-black shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-50">
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Sparkles size={15} />} Generate
            </button>
            <button onClick={nextScene} disabled={busy || !draft.outputAssetId} data-testid="dps-next-scene"
              className="flex items-center justify-center gap-2 border-2 border-black bg-[#00F5D4] py-3 font-mono text-xs font-black uppercase tracking-widest text-black shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-50">
              <Plus size={15} /> Next Scene
            </button>
          </div>
        </div>

        {/* Export */}
        <div className="space-y-2 border-2 border-black bg-[#111] p-3 shadow-[3px_3px_0px_#000]">
          <div className="flex items-center gap-2 font-mono text-[10px] font-black uppercase tracking-widest text-[#FFE08A]"><Film size={14} /> Export Master · 720×1280</div>
          {exportAsset ? (
            <>
              <video src={exportAsset.src} data-testid="dps-export-video" controls autoPlay loop playsInline className="w-full border-2 border-black" />
              <SaveDestinations assetId={exportAsset.id} track="VIDEOS" filename={(projectName || "director").replace(/\s+/g, "_").toLowerCase()} ext="mp4" commit className="mt-1" />
            </>
          ) : (
            <button onClick={doExport} disabled={exporting || scenes.length === 0} data-testid="dps-export"
              className="flex w-full items-center justify-center gap-2 border-2 border-black bg-[#FFE08A] py-3 font-mono text-xs font-black uppercase tracking-widest text-black shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-50">
              {exporting ? <><Loader2 size={15} className="animate-spin" /> {stage || "Baking…"}</> : <><Download size={15} /> Bake & Export ({scenes.length})</>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* Build the SCENE OBJECT (matches the backend data model). */
function buildScene(draft, viewMode, index, saving = false) {
  const isComic = viewMode === "COMIC";
  const animated = isComic ? draft.animate : true;      // cinematic scenes are always video
  const duration = animated ? draft.duration : "STATIC";
  return {
    scene_index: index,
    view_mode: viewMode,
    duration_seconds: duration,
    continuity_lock: !!draft.continuityLock,
    scene_animation_gate: { is_animated: animated, render_length: `${draft.duration}s` },
    audio: {
      whisper_transcript: draft.dialogue || "",
      tts_speaker_id: draft.speaker || "onyx",
      dialogue_line: draft.dialogue || "",
    },
    visuals: {
      user_prompt: draft.prompt || "",
      ai_fixed_prompt: draft.aiFixed || "",
      character_id_lock: draft.characterId || "",
      comic_style: draft.comicStyle || "disney_3d",
      engine_seed: draft.engineSeed || 0,
      output_asset_url: "",
      output_asset_id: saving ? (draft.outputAssetId || "") : "",
      terminal_frame_asset_id: draft.terminalId || "",
      mode: draft.previewMode || (animated ? "video" : "image"),
    },
  };
}
